import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { TrackedJobStatus } from "src/generated/graphqlTypes";
import { usePageVisibility } from "src/hooks/usePageVisibility";
import { useTrackedJobsPollQuery } from "src/hooks/useTrackedJobs.generated";

export type TrackedJobEntry = {
  jobId: string;
  title: string;
  status: TrackedJobStatus | undefined;
  progressPercent: number | undefined;
  progressMessage: string | undefined;
  downloadUrl: string | undefined;
  fileName: string | undefined;
};

type TrackedJobsContextValue = {
  jobs: TrackedJobEntry[];
  addJob: (jobId: string, title: string) => void;
  dismissTrackedJob: (jobId: string) => void;
};

/** Exported for use in Storybook decorators */
export const TrackedJobsContext = createContext<TrackedJobsContextValue>({
  jobs: [],
  addJob: () => {},
  dismissTrackedJob: () => {},
});

export function useTrackedJobs() {
  return useContext(TrackedJobsContext);
}

type TrackedJobMeta = { jobId: string; title: string };

export function TrackedJobsProvider(props: PropsWithChildren<unknown>) {
  const { children } = props;
  const [trackedMeta, setTrackedMeta] = useState<TrackedJobMeta[]>([]);
  const downloadedRef = useRef<Set<string>>(new Set());
  const isVisible = usePageVisibility();

  const jobIds = trackedMeta.map((m) => m.jobId);
  const hasJobs = jobIds.nonEmpty;

  const { data, startPolling, stopPolling } = useTrackedJobsPollQuery({
    variables: { filter: { id: jobIds } },
    skip: !hasJobs,
    fetchPolicy: "network-only",
  });

  const allTerminal =
    hasJobs &&
    data?.trackedJobs.every(
      (j) => j.status.code === TrackedJobStatus.Completed || j.status.code === TrackedJobStatus.Failed,
    );

  // Manage polling based on visibility and terminal status
  useEffect(() => {
    if (!hasJobs || allTerminal) {
      stopPolling();
      return;
    }
    if (isVisible) {
      const interval = data?.trackedJobs.map((j) => j.pollIntervalInMs).min() ?? 2000;
      startPolling(interval);
    } else {
      stopPolling();
    }
  }, [hasJobs, isVisible, allTerminal, data?.trackedJobs, startPolling, stopPolling]);

  // Auto-download completed jobs
  useEffect(() => {
    data?.trackedJobs.forEach((job) => {
      if (
        job.status.code === TrackedJobStatus.Completed &&
        job.result?.attachmentUrl &&
        !downloadedRef.current.has(job.id)
      ) {
        downloadedRef.current.add(job.id);
        const link = document.createElement("a");
        link.href = job.result.attachmentUrl;
        link.download = job.result.fileName ?? "";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  }, [data?.trackedJobs]);

  const addJob = useCallback((jobId: string, title: string) => {
    setTrackedMeta((prev) => {
      if (prev.some((m) => m.jobId === jobId)) return prev;
      return [...prev, { jobId, title }];
    });
  }, []);

  const dismissTrackedJob = useCallback((jobId: string) => {
    setTrackedMeta((prev) => prev.filter((m) => m.jobId !== jobId));
    downloadedRef.current.delete(jobId);
  }, []);

  const jobs = useMemo<TrackedJobEntry[]>(
    () =>
      trackedMeta.map((meta) => {
        const job = data?.trackedJobs.find((j) => j.id === meta.jobId);
        const progressPercent =
          job?.progressCurrent != null && job?.progressTotal
            ? Math.round((job.progressCurrent / job.progressTotal) * 100)
            : undefined;
        return {
          jobId: meta.jobId,
          title: meta.title,
          status: job?.status.code,
          progressPercent,
          progressMessage: job?.progressMessage ?? undefined,
          downloadUrl: job?.result?.attachmentUrl,
          fileName: job?.result?.fileName ?? undefined,
        };
      }),
    [data, trackedMeta],
  );

  return (
    <TrackedJobsContext.Provider value={{ jobs, addJob, dismissTrackedJob }}>{children}</TrackedJobsContext.Provider>
  );
}
